import {HttpError, QueryParameters} from '../api';
import {ApiAuthTokens} from '../types';

type OperationType = 'get' | 'update' | 'remove' | 'create';

export type PartialData<K extends keyof any, T> = {
  [P in K]?: T;
};
export class ApiOperationError extends Error {
  constructor(
    public readonly entityName: string,
    public readonly operation: OperationType,
    public readonly innerError?: HttpError,
    public readonly parameters?: Record<string, unknown>,
  ) {
    super(innerError?.message);
  }
}

export enum ApiOperationStatus {
  'OK',
  'Failed',
}

/**
 * May handle an error.
 * @returns
 * - undefined if completly handled
 * - same or higher level error if none or just partial handling of given error
 */
export type ErrorHandler = (error: Error) => Error | undefined;

export interface ApiOperations<
  TData extends PartialData<TIdKey, any>,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TParameters extends QueryParameters = {},
  TPayload = Record<string, unknown>,
  TIdKey extends keyof TData = keyof TData
> {
  getItem(id: TData[TIdKey]): Promise<void>;
  getList(parameters?: TParameters): Promise<void>;
  update(
    id: TData[TIdKey],
    data: Omit<TData, TIdKey>,
  ): Promise<ApiOperationStatus>;
  remove(id: TData[TIdKey]): Promise<ApiOperationStatus>;
  create(
    payload: TPayload,
  ): Promise<{status: ApiOperationStatus; data?: TData}>;
}
export interface ApiOperationsWithAuthToken<
  TData extends PartialData<TIdKey, any>,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TParameters extends QueryParameters = {},
  TPayload = Record<string, unknown>,
  TIdKey extends keyof TData = keyof TData
> {
  getItem(id: TData[TIdKey], authTokens?: ApiAuthTokens): Promise<void>;
  getList(parameters?: TParameters, authTokens?: ApiAuthTokens): Promise<void>;
  update(
    id: TData[TIdKey],
    data: Omit<TData, TIdKey>,
    authTokens?: ApiAuthTokens,
  ): Promise<ApiOperationStatus>;
  remove(
    id: TData[TIdKey],
    authTokens?: ApiAuthTokens,
  ): Promise<ApiOperationStatus>;
  create(
    payload: TPayload,
    authTokens?: ApiAuthTokens,
  ): Promise<{status: ApiOperationStatus; data?: TData}>;
}

export type LoadStatus = 'NotLoaded' | 'Loading' | 'Loaded' | 'Failed';
